import { createContext, useContext } from 'react';

import type { EmailVerification } from './useEmailVerification';

/**
 * The ZeroBounce store, from {@link ./FormShell} down to whichever leaf is
 * rendering an email input — a scalar {@link ./FieldRenderer} field or a
 * {@link ./RepeatableRowControl} cell.
 *
 * A context rather than a prop because the consumers are two unrelated leaves
 * several levels down, and the alternative is threading a pair of callbacks
 * through every intermediate renderer the way `addressAutocomplete` is
 * threaded. That prop is a boolean a parent can forget to pass without anything
 * breaking loudly; this is a live store, and a dropped link would silently mean
 * "no verdict", which passes. The existing `ShowErrorsContext` and
 * `DateFormatContext` exist for the same reason.
 *
 * <h3>Null is the capability switch</h3>
 * `null` means this runtime may not verify at all — the public customer
 * runtime, where `/integrations/email/verify` is authenticated on purpose
 * (AnonymousSurfaceTests pins it beside Canada Post: a paid proxy an anonymous
 * caller reaching it is a direct bill). Leaves check for null and do nothing,
 * so no mode has to travel down here; see `formShellMode.ts` on why capability
 * and audience are kept apart.
 *
 * It is also the default, which makes every test and every renderer mounted
 * outside a FormShell inert rather than reaching for a mock API client.
 */
export const EmailVerifyContext = createContext<EmailVerification | null>(null);

/** The store for the current runtime, or null when verification is switched off. */
export function useEmailVerify(): EmailVerification | null {
  return useContext(EmailVerifyContext);
}
